/**
 * Shared chrome for the admin dashboard — wooden panels, pixel buttons and
 * tables built out of the same tokens/classes as the rest of the game UI
 * (`.tb-pixel-btn`, `.game-font`, surface/border/gold tokens). Everything is
 * mobile-first: tables scroll horizontally inside their panel, grids collapse
 * to one column on phones.
 */
import { el, clear, toast } from '../../dom';
// Re-exported so admin views keep importing their download helper from here; the
// single implementation lives in dom.ts (its anchor must stay detached — see there).
export { saveBlob } from '../../dom';
import { ApiError } from '../../../core/network/api';
import { handleSessionExpiry } from '../../session-expiry';

export const LABEL = 'text-[10px] uppercase tracking-widest text-text-dim font-mono mb-1';

export function panel(title: string, ...children: (Node | string | null)[]): HTMLElement {
  return el('section', 'game-font rounded-xl border-2 border-[#5a4632] bg-surface/95 shadow-lift overflow-hidden', {},
    el('header', 'px-4 py-2.5 border-b-2 border-[#5a4632] bg-[#322820] text-[#ffd97a] text-sm', {}, title),
    el('div', 'p-4', {}, ...children.filter((c): c is Node | string => c != null)));
}

export function statCard(label: string, value: string, hint?: string): HTMLElement {
  return el('div', 'game-font rounded-xl border-2 border-[#5a4632] bg-[#322820] px-4 py-3 min-w-0', {},
    el('div', LABEL, {}, label),
    el('div', 'text-2xl text-[#ffd97a] font-mono leading-tight truncate', { title: value }, value),
    hint ? el('div', 'text-[11px] text-text-dim mt-0.5 truncate', {}, hint) : null);
}

export function grid(min = 200): HTMLElement {
  return el('div', 'grid gap-3', { style: `grid-template-columns: repeat(auto-fill, minmax(${min}px, 1fr));` });
}

export function btn(label: string, onClick: () => void, variant: 'default' | 'danger' | 'primary' = 'default'): HTMLButtonElement {
  const tone = variant === 'danger'
    ? ' !text-[#ffb3a7] !border-[#8a3b2e] hover:!border-error'
    : variant === 'primary' ? ' !text-[#ffe3a6] !border-[#8a6a45]' : '';
  return el('button', `tb-pixel-btn px-3 py-1.5 text-xs${tone}`, { type: 'button', onClick }, label);
}

export function input(placeholder: string, value = '', type = 'text'): HTMLInputElement {
  return el('input',
    'w-full px-2.5 py-1.5 rounded-lg bg-surface-2 border border-border text-sm text-text ' +
    'outline-none focus:border-gold placeholder:text-text-dim/60',
    { type, value, placeholder });
}

export function textarea(placeholder: string, value = '', rows = 5): HTMLTextAreaElement {
  return el('textarea',
    'w-full px-2.5 py-2 rounded-lg bg-surface-2 border border-border text-sm text-text ' +
    'outline-none focus:border-gold placeholder:text-text-dim/60 resize-y',
    { rows, value, placeholder });
}

export function checkbox(label: string, checked = false): { wrap: HTMLElement; input: HTMLInputElement } {
  const box = el('input', 'accent-[#c9a84c] w-4 h-4', { type: 'checkbox', checked });
  const wrap = el('label', 'flex items-center gap-2 text-sm cursor-pointer select-none', {}, box, label);
  return { wrap, input: box };
}

export function labelled(label: string, control: HTMLElement): HTMLElement {
  return el('div', 'min-w-0', {}, el('div', LABEL, {}, label), control);
}

/** A field-level error line, toggled by the form validators. */
export function fieldError(): HTMLElement {
  const node = el('div', 'text-[11px] text-error mt-1 hidden');
  return node;
}
export function setFieldError(node: HTMLElement, message?: string) {
  node.textContent = message ?? '';
  node.classList.toggle('hidden', !message);
}

// ─── Async states ────────────────────────────────────────────────────────────

export function spinner(text = 'Loading…'): HTMLElement {
  return el('div', 'py-6 text-center text-sm text-text-dim game-font', {}, text);
}

export function errorBox(message: string, onRetry?: () => void): HTMLElement {
  return el('div', 'game-font rounded-lg border-2 border-[#8a3b2e] bg-[#2a1512] p-4 text-sm text-[#ffb3a7] flex flex-wrap items-center gap-3', {},
    el('span', 'flex-1 min-w-0', {}, message),
    onRetry ? btn('Retry', onRetry) : null);
}

export function emptyBox(message: string): HTMLElement {
  return el('div', 'py-6 text-center text-sm text-text-dim game-font', {}, message);
}

/**
 * The message an admin screen shows for a failed request — and the one place the
 * panel notices it has been signed out.
 *
 * `handleSessionExpiry` was wired into the farm views only, so a 401 in the ADMIN
 * panel produced "Could not save" and nothing else: no toast explaining the
 * session had expired, no route to the sign-in screen, and an editor still full
 * of unsaved text. Reported as a save that silently would not work — the
 * session had aged out, and the panel had no way to say so.
 *
 * For an inline error panel it returns a sentence explaining what happened; for a
 * TOAST use `toastError`, which stays silent because `handleSessionExpiry` posts
 * its own message and two saying the same thing is worse than one.
 */
export function apiMessage(e: unknown, fallback: string): string {
  if (handleSessionExpiry(e)) return 'Your session has expired — signing you out.';
  if (e instanceof ApiError) {
    if (e.status === 404 || e.status === 0) return `${fallback} (endpoint not available yet)`;
    if (e.status === 403) return 'You do not have permission to view this.';
    return e.message || fallback;
  }
  return fallback;
}

/**
 * Toast a failed request, unless the session-expiry path has already taken over —
 * it posts "Your session expired — please sign in again" and routes to the sign-in
 * screen, and a second toast on top of that only muddies it.
 */
export function toastError(e: unknown, fallback: string): void {
  if (handleSessionExpiry(e)) return;
  toast(apiMessage(e, fallback), 'error');
}

/**
 * Loads `fetcher()` into `host`, showing a spinner then either the rendered
 * content or a retry-able error box. Never throws — a missing backend shows a
 * clear message instead of a blank panel or an unhandled rejection.
 */
export function loadInto<T>(
  host: HTMLElement,
  fetcher: () => Promise<T>,
  render: (data: T, reload: () => void) => Node,
  fallback = "Couldn't load this section.",
): () => void {
  let cancelled = false;
  const run = () => {
    clear(host);
    host.appendChild(spinner());
    fetcher().then((data) => {
      if (cancelled) return;
      clear(host);
      host.appendChild(render(data, run));
    }).catch((e) => {
      if (cancelled) return;
      clear(host);
      host.appendChild(errorBox(apiMessage(e, fallback), run));
    });
  };
  run();
  return () => { cancelled = true; };
}

// ─── Table ───────────────────────────────────────────────────────────────────

export function table(headers: string[], rows: (Node | string)[][]): HTMLElement {
  const thead = el('tr', '', {},
    ...headers.map((h) => el('th', 'text-left font-normal text-[10px] uppercase tracking-widest text-text-dim px-2 py-2 whitespace-nowrap', {}, h)));
  const body = el('tbody');
  for (const r of rows) {
    body.appendChild(el('tr', 'border-t border-border/60 hover:bg-[#322820]/60', {},
      ...r.map((c) => el('td', 'px-2 py-2 text-sm align-middle whitespace-nowrap', {}, c))));
  }
  return el('div', 'overflow-x-auto -mx-1', {},
    el('table', 'w-full min-w-[560px] border-collapse', {}, el('thead', '', {}, thead), body));
}

export function pill(text: string, tone: 'ok' | 'warn' | 'bad' | 'mute' = 'mute'): HTMLElement {
  const cls = tone === 'ok' ? 'text-leaf border-leaf/50'
    : tone === 'warn' ? 'text-gold border-gold/50'
      : tone === 'bad' ? 'text-error border-error/50' : 'text-text-dim border-border';
  return el('span', `inline-block text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${cls}`, {}, text);
}

// ─── Modal / confirm ─────────────────────────────────────────────────────────

/**
 * Every modal currently on screen, by its dismiss function.
 *
 * A modal's layer is appended to `document.body`, not to the view — it has to be,
 * to escape the panel's stacking and scrolling — which means a view teardown does
 * NOT take it with it. Leaving the admin section (switching tab, or the router
 * dropping /play when the session ends) therefore stranded an open editor on top
 * of whatever rendered next. Reported as an "Edit — Legal pages" dialog sitting on
 * the landing page after being signed out, still offering a Save button that could
 * no longer save anything.
 */
const openModals = new Set<() => void>();

/**
 * Dismiss every open modal. Dismiss, not close: `confirmDialog` resolves `false`
 * on a dismiss, so a forced teardown answers its promise instead of leaving the
 * caller waiting on a decision that can never come.
 */
export function closeAllModals(): void {
  for (const dismiss of [...openModals]) dismiss();
  openModals.clear();
}

/**
 * How wide a modal may grow. `default` is the confirm/short-form box; `wide` is for
 * the dense two-column forms (player card, invoice editor), which at `max-w-md`
 * squeezed every input to a third of its useful width.
 */
export type ModalSize = 'default' | 'wide';
export const MODAL_WIDTH: Record<ModalSize, string> = { default: 'max-w-md', wide: 'max-w-5xl' };

export interface ModalOptions { size?: ModalSize }

/**
 * The modal's layer and box classes.
 *
 * Tall-modal trap (a modal taller than the screen whose top — close button included —
 * is pushed out of reach): it happens when the OVERLAY scrolls while it also centres
 * its child. Here the overlay never scrolls; the box is capped at 90vh and only its
 * BODY scrolls, with header and footer pinned. So the × and the actions are always on
 * screen, centred when the box fits, at any width.
 */
export function modalClasses(size: ModalSize = 'default'): { layer: string; box: string; body: string } {
  return {
    layer: 'tb-game-modal fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-3',
    box: `game-font w-full ${MODAL_WIDTH[size]} rounded-xl border-2 border-[#5a4632] bg-surface shadow-lift overflow-hidden max-h-[90vh] flex flex-col`,
    body: 'p-4 overflow-y-auto min-h-0',
  };
}

export function modal(
  title: string, body: HTMLElement, actions: HTMLElement[], onDismiss?: () => void, options: ModalOptions = {},
): () => void {
  const cls = modalClasses(options.size);
  // `tb-game-modal` is the class bootstrap.ts sweeps off document.body when the
  // game unmounts. The admin dialog did not carry it, which is why it survived a
  // sign-out that the farm's own overlays did not — same bug, twice, because the
  // convention was undocumented anywhere but that one sweep.
  const layer = el('div', cls.layer);
  // Escape + an × in the header: the backdrop click was the ONLY way out, and a
  // dialog whose body fills the viewport (long forms on a phone) leaves no backdrop
  // to click. The listener is capture-phase so admin inputs can't swallow it, and it
  // is removed with the layer so a closed modal never keeps a key handler alive.
  const close = () => {
    document.removeEventListener('keydown', onKey, true);
    openModals.delete(dismiss);
    layer.remove();
  };
  // A DISMISS (×, Escape, backdrop) is not the same as the caller closing the modal
  // itself: confirmDialog must resolve `false` here, or an Escape would leave its
  // promise pending forever and the caller waiting on a decision that never lands.
  const dismiss = () => { close(); onDismiss?.(); };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); dismiss(); } };
  document.addEventListener('keydown', onKey, true);
  const box = el('div', cls.box, {},
    el('header', 'px-4 py-2.5 border-b-2 border-[#5a4632] bg-[#322820] text-[#ffd97a] text-sm flex items-center gap-2', {},
      el('span', 'flex-1 min-w-0 truncate', {}, title),
      el('button', 'shrink-0 text-lg leading-none text-[#ffd97a]/70 hover:text-[#ffd97a]', { title: 'Close (Esc)', onClick: dismiss }, '×')),
    el('div', cls.body, {}, body),
    el('div', 'flex justify-end gap-2 px-4 py-3 border-t-2 border-[#5a4632]', {}, ...actions));
  layer.addEventListener('click', (e) => { if (e.target === layer) dismiss(); });
  layer.appendChild(box);
  document.body.appendChild(layer);
  openModals.add(dismiss);
  return close;
}

/** Destructive actions go through here first. Resolves true when confirmed. */
export function confirmDialog(title: string, message: string, confirmLabel = 'Confirm'): Promise<boolean> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (v: boolean) => { if (done) return; done = true; close(); resolve(v); };
    const close = modal(title,
      el('div', 'text-sm text-text', {}, message),
      [btn('Cancel', () => finish(false)), btn(confirmLabel, () => finish(true), 'danger')],
      () => finish(false));
  });
}


/**
 * A button for a destructive or money action that needs a second click: the first
 * click arms it ("CLICK TO CONFIRM"), the second runs it. It disarms itself after a
 * few seconds, so a stray click minutes later never fires. While the action runs the
 * button is disabled, so a double click cannot run it twice.
 */
export function confirmBtn(
  label: string,
  onConfirm: () => void | Promise<void>,
  opts: { variant?: 'default' | 'danger' | 'primary'; armedLabel?: string; timeoutMs?: number } = {},
): HTMLButtonElement {
  const armedLabel = opts.armedLabel ?? 'CLICK TO CONFIRM';
  let armed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const disarm = () => {
    armed = false;
    if (timer) clearTimeout(timer);
    timer = undefined;
    b.textContent = label;
    b.removeAttribute('data-armed');
  };
  const b = btn(label, () => {
    if (b.disabled) return;
    if (!armed) {
      armed = true;
      b.textContent = armedLabel;
      b.setAttribute('data-armed', 'true');
      timer = setTimeout(disarm, opts.timeoutMs ?? 4000);
      return;
    }
    disarm();
    b.disabled = true;
    void Promise.resolve()
      .then(onConfirm)
      .catch(() => undefined)
      .finally(() => { b.disabled = false; });
  }, opts.variant ?? 'danger');
  return b;
}
